import { Account, Flag, Organization, Service, Variant } from '@acies/domain';
import { IFlagJSON, IVariantJSON } from '@acies/schema';
import AuthorizationError from '@shared/errors/authorization-error';
import InternalServerError from '@shared/errors/internal-server-error';
import NetworkError from '@shared/errors/network-error';
import ParseError from '@shared/errors/parse-error';
import UnknownError from '@shared/errors/unknown-error';
import UnSupportedMediaTypeError from '@shared/errors/unsupported-media-type-error';
import { OrganizationVariant } from '../domain/organization-variant';

type Errors = ParseError | NetworkError | AuthorizationError | InternalServerError | UnknownError | UnSupportedMediaTypeError | null;

export class FeatureFlagsAdminModel {
	public accountsMap: Map<string, Account>;
	public featureFlagsUIVisible: boolean;
	public globalSpinnerVisible: boolean;
	public organizations: Organization[];
	public selectedService: Service | null;
	public selectedFlag: Flag | null;
	public services: Service[];
	public organizationVariants: OrganizationVariant[];
	public networkErrorDialog: HTMLDialogElement | null;
	public unknownErrorDialog: HTMLDialogElement | null;
	public confirmationDialog: HTMLDialogElement | null;
	public addVariantsDialog: HTMLDialogElement | null;
	public updatingFlags: boolean;
	public addVariantsButtonEnabled: boolean;
	public addVariantsTopLevelButtonEnabled: boolean;
	public confirmBody: string;
	public selectedOrganization: Organization | null;
	public organizationAccounts: Account[];
	public selectedAccounts: Account[];
	public addVariantsSpinnerVisible: boolean;
	public addVariantsCancelButtonEnabled: boolean;
	public pendingVariants: Variant[];
	#dateTimeFormatter: Intl.DateTimeFormat;
	public updatingInstance: Flag | Variant | null;
	#today: Date;
	public constructor() {
		this.accountsMap = new Map();
		this.featureFlagsUIVisible = false;
		this.globalSpinnerVisible = true;
		this.organizations = [];
		this.organizationVariants = [];
		this.selectedService = null;
		this.services = [];
		this.#dateTimeFormatter = new Intl.DateTimeFormat('da-DK', { dateStyle: 'long' });
		this.#today = new Date();
		this.updatingFlags = false;
		this.addVariantsButtonEnabled = false;
		this.addVariantsTopLevelButtonEnabled = false;
		this.confirmBody = '';
		this.selectedOrganization = null;
		this.organizationAccounts = [];
		this.selectedAccounts = [];
		this.addVariantsSpinnerVisible = false;
		this.addVariantsCancelButtonEnabled = true;
		this.pendingVariants = [];
		this.updatingInstance = null;
	}

	public toDateString(value: Date): string {
		return this.#dateTimeFormatter.format(value);
	}

	public toDaysLeft(value: Date): number {
		const delta = value.getTime() - this.#today.getTime();
		if (delta <= 0) {
			return 0;
		}
		const ONE_DAY_IN_MILLI = 24 * 60 * 60 * 1000;
		return Math.ceil(delta / ONE_DAY_IN_MILLI);
	}

	public get modelAsBodyJSON(): IFlagJSON[] {
		const flags: IFlagJSON[] = [];
		for (const service of this.services) {
			for (const flag of service.flags) {
				flags.push(this.#getFlagJSON(service, flag));
			}
		}
		return flags;
	}

	/**
	 * When a Flag instance is selected, create OrganizationVariants for the UI to render.
	 *
	 * Since a Variant can span across Orginations, we create a UI specific OrganizationVariant instance that groups Variants by Organization.
	 */
	public updateOrganizationVariants(): void {
		if (this.selectedFlag) {
			// to group by Organization, we create a Map where the Organization is the key
			// so we can push variants to the OrganizationVariant.variants Array.
			const organizationVariants = new Map<Organization, OrganizationVariant>();
			const flag = this.selectedFlag;
			for (const variant of flag.variants) {
				const organization = variant.account.organization;
				let organizationVariant = organizationVariants.get(organization);
				if (!organizationVariant) {
					organizationVariant = new OrganizationVariant({ organization, variants: [] });
					organizationVariants.set(organization, organizationVariant);
				}
				organizationVariant.variants.push(variant);
			}
			this.organizationVariants = Array.from(organizationVariants.values());
		} else {
			this.organizationVariants = [];
		}
	}

	#getFlagJSON(service: Service, flag: Flag): IFlagJSON {
		return {
			createdAt: flag.createdAt.toISOString(),
			description: flag.description,
			enabled: flag.enabled,
			expiresAt: flag.expiresAt.toISOString(),
			id: flag.name,
			service: service.name,
			variants: this.#getVariantsJSON(flag.variants),
		};
	}

	#getVariantsJSON(variants: Variant[]): IVariantJSON[] {
		const json: IVariantJSON[] = [];
		for (const variant of variants) {
			json.push({
				accountId: variant.account.id,
				enabled: variant.enabled,
			});
		}
		return json;
	}

	public hideNetworkErrorDialog(): void {
		if (this.networkErrorDialog) {
			this.networkErrorDialog.close();
		}
	}

	public hideUnknownErrorDialog(): void {
		if (this.unknownErrorDialog) {
			this.unknownErrorDialog.close();
		}
	}

	public showNetworkErrorDialog(): void {
		if (this.networkErrorDialog) {
			this.networkErrorDialog.showModal();
		}
	}

	public showUnknownErrorDialog(): void {
		if (this.unknownErrorDialog) {
			this.unknownErrorDialog.showModal();
		}
	}

	public showConfirmationDialog(): void {
		if (this.confirmationDialog) {
			this.confirmationDialog.showModal();
		}
	}

	public hideConfirmationDialog(): void {
		if (this.confirmationDialog) {
			this.confirmationDialog.close();
		}
	}

	public displayErrorDialogFromError(error: Errors = null): void {
		if (error instanceof NetworkError) {
			this.showNetworkErrorDialog();
		} else {
			this.showUnknownErrorDialog();
		}
	}

	public showAddVariantsDialog(): void {
		if (this.addVariantsDialog) {
			this.addVariantsDialog.showModal();
		}
	}

	public hideAddVariantsDialog(): void {
		if (this.addVariantsDialog) {
			this.addVariantsDialog.close();
		}
	}
}
